import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, getAuthHeaders } from '../../lib/authFetch';
import { TrendingUp, TrendingDown, Clock, CheckCircle, ChevronRight, Radio, Target, BarChart2, Zap, ArrowLeft } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { getBalance, getCachedBalance } from '../../services/walletService';
import Layout from '../../components/Layout/Layout';
import '../../styles/dashboard.css'

const BASE = import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/api/signals`
    : 'http://localhost:5000/api/signals';
const SOCKET_URL = import.meta.env.VITE_P2P_SOCKET_URL || 'http://localhost:5000';

async function api(method: string, path: string, body?: object) {
    const res = await authFetch(`${BASE}${path}`, {
        method, headers: getAuthHeaders(),
        body: body ? JSON.stringify(body) : undefined,
    });
    return res.json();
}

interface Signal {
    _id: string; pair: string; coin: string;
    type: 'spot' | 'futures';
    direction: 'long' | 'short' | 'buy' | 'sell';
    entryPrice: number; targetProfitPercent: number; stopLossPercent: number;
    leverage?: number; status: 'active' | 'closed' | 'cancelled';
    notes?: string; broadcastedAt?: string; closedAt?: string;
    closePrice?: number; actualProfitPercent?: number; followersCount?: number;
}

interface Position {
    following: boolean; allocatedAmount?: number; entryPrice?: number;
    pnlPercent?: number; pnlAmount?: number; settled?: boolean;
}

function PnLBadge({ value }: { value: number }) {
    const pos = value >= 0;
    return (
        <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 13, fontWeight: 700, background: pos ? 'rgba(28,212,167,0.15)' : 'rgba(246,70,93,0.15)', color: pos ? '#1CD4A7' : '#F6465D' }}>
            {pos ? '+' : ''}{value.toFixed(2)}%
        </span>
    );
}

function DirBadge({ dir }: { dir: string }) {
    const green = ['buy','long'].includes(dir);
    return (
        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: green ? 'rgba(28,212,167,0.15)' : 'rgba(246,70,93,0.15)', color: green ? '#1CD4A7' : '#F6465D' }}>
            {dir.toUpperCase()}
        </span>
    );
}

export default function SignalsPage() {
    const navigate = useNavigate();
    const [signals, setSignals] = useState<Signal[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Signal | null>(null);
    const [position, setPosition] = useState<Position | null>(null);
    const [tab, setTab] = useState<'active' | 'closed'>('active');
    const [allocAmount, setAllocAmount] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [posLoading, setPosLoading] = useState(false);
    const [availableBalance, setAvailableBalance] = useState(0);
    const [balanceLoading, setBalanceLoading] = useState(true);
    const socketRef = useRef<Socket | null>(null);

    const loadSignals = async () => {
        setLoading(true);
        try {
            const d = await api('GET', '/');
            const raw = d.data;
            setSignals(Array.isArray(raw) ? raw : raw?.signals ?? []);
        } catch { setSignals([]); }
        setLoading(false);
    };

    useEffect(() => {
        loadSignals();
        setAvailableBalance(getCachedBalance());
        getBalance().then(d => setAvailableBalance(d.balance || 0)).catch(() => {}).finally(() => setBalanceLoading(false));
        const token = (() => { try { return JSON.parse(localStorage.getItem('userInfo') || '{}').token; } catch { return null; } })();
        const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket','polling'] });
        socketRef.current = socket;
        socket.on('signal:new', loadSignals);
        socket.on('signal:closed', ({ signalId, actualProfitPercent, closePrice }: any) => {
            setSignals(prev => prev.map(s => s._id === signalId ? { ...s, status: 'closed', actualProfitPercent, closePrice } : s));
            setSelected(prev => {
                if (!prev || prev._id !== signalId) return prev;
                api('GET', `/${signalId}/my-position`).then(d => setPosition(d.data)).catch(() => {});
                return { ...prev, status: 'closed' as const, actualProfitPercent, closePrice };
            });
        });
        return () => { socket.disconnect(); };
    }, []);

    const openDetail = async (sig: Signal) => {
        setSelected(sig);
        setPosLoading(true);
        try { const d = await api('GET', `/${sig._id}/my-position`); setPosition(d.data); }
        catch { setPosition(null); }
        setPosLoading(false);
    };

    const handleFollow = async () => {
        if (!selected || !allocAmount) return;
        setActionLoading(true);
        try {
            await api('POST', `/${selected._id}/follow`, { allocatedAmount: parseFloat(allocAmount) });
            const d = await api('GET', `/${selected._id}/my-position`); setPosition(d.data);
            setAllocAmount(''); getBalance().then(d => setAvailableBalance(d.balance || 0)).catch(() => setAvailableBalance(getCachedBalance()));
        } catch { }
        setActionLoading(false);
    };

    const handleUnfollow = async () => {
        if (!selected) return;
        setActionLoading(true);
        try {
            await api('POST', `/${selected._id}/unfollow`);
            const d = await api('GET', `/${selected._id}/my-position`); setPosition(d.data);
            getBalance().then(d => setAvailableBalance(d.balance || 0)).catch(() => setAvailableBalance(getCachedBalance()));
        } catch { }
        setActionLoading(false);
    };

    const activeSignals = signals.filter(s => s.status === 'active');
    const closedSignals = signals.filter(s => s.status === 'closed');
    const winners = closedSignals.filter(s => (s.actualProfitPercent ?? 0) > 0);
    const winRate = closedSignals.length > 0 ? Math.round((winners.length / closedSignals.length) * 100) : 0;
    const avgReturn = closedSignals.length > 0
        ? closedSignals.reduce((s, x) => s + (x.actualProfitPercent ?? 0), 0) / closedSignals.length : 0;
    const displayed = tab === 'active' ? activeSignals : closedSignals;

    const stats = [
        { label: 'Total Signals', value: signals.length, icon: Radio, color: '#1CD4A7' },
        { label: 'Active Now',    value: activeSignals.length, icon: Zap, color: '#F0B90B' },
        { label: 'Win Rate',      value: `${winRate}%`, icon: Target, color: '#1CD4A7' },
        { label: 'Avg Return',    value: `${avgReturn >= 0 ? '+' : ''}${avgReturn.toFixed(2)}%`, icon: BarChart2, color: avgReturn >= 0 ? '#1CD4A7' : '#F6465D' },
    ];

    return (
        <Layout activePage="signals">
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(16px, 4vw, 24px)' }}>

                {/* Header */}
                {selected ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                        <button onClick={() => setSelected(null)} style={{ background: 'var(--ui-bg-elevated)', border: 'none', borderRadius: 8, padding: '8px 10px', color: 'var(--ui-text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <ArrowLeft size={18} />
                        </button>
                        <div>
                            <h1 style={{ fontSize: 'clamp(16px,4vw,22px)', fontWeight: 700, color: 'var(--ui-text-primary)', margin: 0 }}>{selected.pair} Signal</h1>
                            <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, margin: 0 }}>Signal detail</p>
                        </div>
                    </div>
                ) : (
                    <div style={{ marginBottom: 20 }}>
                        <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', color: 'var(--ui-text-primary)', cursor: 'pointer', padding: '0 0 12px', gap: 6 }} className="mobile-only">
                            <ArrowLeft size={20} />
                        </button>
                        <h1 style={{ fontSize: 'clamp(18px,4vw,24px)', fontWeight: 700, color: 'var(--ui-text-primary)', margin: '0 0 4px' }}>Copy Trading</h1>
                        <p style={{ color: 'var(--ui-text-muted)', fontSize: 13, margin: 0 }}>Follow signals to allocate funds. P&L settled automatically when signal closes.</p>
                    </div>
                )}

                {!selected && (
                    <>
                        {/* Stat Cards — 2x2 on mobile, 4x1 on desktop */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
                            {stats.map(card => (
                                <div key={card.label} style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'var(--ui-text-muted)', fontSize: 12 }}>{card.label}</span>
                                        <div style={{ background: `${card.color}18`, borderRadius: 6, padding: 6 }}>
                                            <card.icon size={14} color={card.color} />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 'clamp(20px,5vw,28px)', fontWeight: 700, color: 'var(--ui-text-primary)' }}>{card.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* Tabs */}
                        <div style={{ display: 'flex', borderBottom: '1px solid #1C1C2C', marginBottom: 16 }}>
                            {(['active','closed'] as const).map(t => (
                                <button key={t} onClick={() => setTab(t)} style={{
                                    padding: '10px 20px', background: 'none', border: 'none',
                                    borderBottom: tab === t ? '2px solid #1CD4A7' : '2px solid transparent',
                                    color: tab === t ? '#1CD4A7' : '#71717A',
                                    fontSize: 14, fontWeight: tab === t ? 600 : 400,
                                    cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s', textTransform: 'capitalize',
                                }}>
                                    {t === 'active' ? 'Active' : 'Closed'}
                                    <span style={{ marginLeft: 6, background: tab === t ? 'rgba(28,212,167,0.15)' : '#1a1a24', color: tab === t ? '#1CD4A7' : '#71717A', borderRadius: 9999, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
                                        {t === 'active' ? activeSignals.length : closedSignals.length}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Signal List */}
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ui-text-muted)' }}>Loading...</div>
                        ) : displayed.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ui-text-muted)' }}>
                                <Radio size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
                                <div>No {tab} signals</div>
                            </div>
                        ) : (
                            /* Mobile: cards, Desktop: rows */
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))', gap: 12 }}>
                                {displayed.map(sig => {
                                    const green = ['buy','long'].includes(sig.direction);
                                    return (
                                        <div key={sig._id} onClick={() => openDetail(sig)} style={{
                                            background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12,
                                            padding: 16, cursor: 'pointer', transition: 'border-color 0.15s',
                                        }}
                                            onMouseEnter={e => (e.currentTarget.style.borderColor = '#2a2a3a')}
                                            onMouseLeave={e => (e.currentTarget.style.borderColor = '#1C1C2C')}
                                        >
                                            {/* Card top row */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={{ background: green ? 'rgba(28,212,167,0.1)' : 'rgba(246,70,93,0.1)', borderRadius: 8, padding: 8 }}>
                                                        {green ? <TrendingUp size={16} color="#1CD4A7" /> : <TrendingDown size={16} color="#F6465D" />}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: 'var(--ui-text-primary)', fontSize: 15 }}>{sig.pair}</div>
                                                        <div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{sig.type?.toUpperCase()}</div>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                    <DirBadge dir={sig.direction} />
                                                    <ChevronRight size={14} color="#52525B" />
                                                </div>
                                            </div>

                                            {/* Stats row */}
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, background: 'var(--ui-bg-primary)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
                                                <div>
                                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 10, marginBottom: 2 }}>Entry</div>
                                                    <div style={{ color: 'var(--ui-text-primary)', fontSize: 12, fontWeight: 600 }}>${sig.entryPrice?.toLocaleString()}</div>
                                                </div>
                                                <div>
                                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 10, marginBottom: 2 }}>Target</div>
                                                    <div style={{ color: '#1CD4A7', fontSize: 12, fontWeight: 600 }}>+{sig.targetProfitPercent}%</div>
                                                </div>
                                                <div>
                                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 10, marginBottom: 2 }}>Stop Loss</div>
                                                    <div style={{ color: '#F6465D', fontSize: 12, fontWeight: 600 }}>-{sig.stopLossPercent}%</div>
                                                </div>
                                            </div>

                                            {/* Bottom row */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                    {sig.leverage && <span style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>{sig.leverage}x leverage</span>}
                                                    {sig.followersCount != null && <span style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>· {sig.followersCount} followers</span>}
                                                </div>
                                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                    {sig.actualProfitPercent !== undefined && <PnLBadge value={sig.actualProfitPercent} />}
                                                    {sig.status === 'active'
                                                        ? <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#F59E0B', fontSize: 11 }}><Clock size={11} />Active</span>
                                                        : <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#1CD4A7', fontSize: 11 }}><CheckCircle size={11} />Closed</span>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Detail View */}
                {selected && (
                    <div style={{ background: 'var(--ui-bg-card)', border: '1px solid var(--ui-border)', borderRadius: 12, padding: 'clamp(16px,4vw,24px)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ background: ['buy','long'].includes(selected.direction) ? 'rgba(28,212,167,0.1)' : 'rgba(246,70,93,0.1)', borderRadius: 8, padding: 10 }}>
                                    {['buy','long'].includes(selected.direction) ? <TrendingUp size={20} color="#1CD4A7" /> : <TrendingDown size={20} color="#F6465D" />}
                                </div>
                                <div>
                                    <div style={{ fontSize: 'clamp(16px,4vw,20px)', fontWeight: 700, color: 'var(--ui-text-primary)' }}>{selected.pair}</div>
                                    <div style={{ fontSize: 12, color: 'var(--ui-text-muted)' }}>{selected.type?.toUpperCase()} · {selected.followersCount ?? 0} followers</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <DirBadge dir={selected.direction} />
                                {selected.status === 'active' ? <Clock size={16} color="#F59E0B" /> : <CheckCircle size={16} color="#1CD4A7" />}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 12, background: 'var(--ui-bg-primary)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                            {[
                                { label: 'Entry Price', value: `$${selected.entryPrice?.toLocaleString()}` },
                                { label: 'Target',      value: `+${selected.targetProfitPercent}%` },
                                { label: 'Stop Loss',   value: `-${selected.stopLossPercent}%` },
                                ...(selected.leverage ? [{ label: 'Leverage', value: `${selected.leverage}x` }] : []),
                                ...(selected.closePrice ? [{ label: 'Close Price', value: `$${selected.closePrice?.toLocaleString()}` }] : []),
                                ...(selected.actualProfitPercent !== undefined ? [{ label: 'Actual P&L', value: `${selected.actualProfitPercent >= 0 ? '+' : ''}${selected.actualProfitPercent.toFixed(2)}%` }] : []),
                            ].map(item => (
                                <div key={item.label}>
                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 11, marginBottom: 4 }}>{item.label}</div>
                                    <div style={{ color: 'var(--ui-text-primary)', fontSize: 14, fontWeight: 600 }}>{item.value}</div>
                                </div>
                            ))}
                        </div>

                        {selected.notes && (
                            <div style={{ background: 'var(--ui-bg-elevated)', borderRadius: 8, padding: '12px 14px', marginBottom: 20, color: 'var(--ui-text-secondary)', fontSize: 13 }}>
                                {selected.notes}
                            </div>
                        )}

                        {selected.status === 'active' && (
                            <div style={{ borderTop: '1px solid #1C1C2C', paddingTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ui-text-primary)', marginBottom: 14 }}>My Position</div>
                                {posLoading ? (
                                    <div style={{ color: 'var(--ui-text-muted)', fontSize: 13 }}>Loading...</div>
                                ) : position?.following ? (
                                    <div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px,1fr))', gap: 12, background: 'var(--ui-bg-primary)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                                            <div><div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>Allocated</div><div style={{ color: 'var(--ui-text-primary)', fontWeight: 600 }}>${position.allocatedAmount?.toFixed(2)}</div></div>
                                            <div><div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>Entry</div><div style={{ color: 'var(--ui-text-primary)', fontWeight: 600 }}>${position.entryPrice?.toFixed(2)}</div></div>
                                            <div><div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>P&L</div><PnLBadge value={position.pnlPercent ?? 0} /></div>
                                        </div>
                                        <button onClick={handleUnfollow} disabled={actionLoading} style={{ width: '100%', padding: '12px', background: 'rgba(246,70,93,0.12)', border: '1px solid #F6465D', color: '#F6465D', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                            {actionLoading ? 'Processing...' : 'Unfollow Signal'}
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div style={{ background: 'var(--ui-bg-elevated)', border: '1px solid var(--ui-border)', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <input type="number" placeholder="Amount (USDT)" value={allocAmount} onChange={e => setAllocAmount(e.target.value)}
                                                style={{ background: 'none', border: 'none', color: 'var(--ui-text-primary)', fontSize: 14, outline: 'none', width: '100%' }} />
                                            <span style={{ color: 'var(--ui-text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>Avbl: {balanceLoading ? <span className="balance-skeleton balance-skeleton-sm" /> : `$${availableBalance.toFixed(2)}`}</span>
                                        </div>
                                        <button onClick={handleFollow} disabled={actionLoading || !allocAmount} style={{ width: '100%', padding: '12px', background: '#1CD4A7', border: 'none', color: '#000', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: !allocAmount ? 0.5 : 1 }}>
                                            {actionLoading ? 'Processing...' : 'Follow Signal'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {selected.status === 'closed' && position?.settled && (
                            <div style={{ borderTop: '1px solid #1C1C2C', paddingTop: 20 }}>
                                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ui-text-primary)', marginBottom: 10 }}>Settled Position</div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px,1fr))', gap: 12, background: 'var(--ui-bg-primary)', borderRadius: 10, padding: 14 }}>
                                    <div><div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>Allocated</div><div style={{ color: 'var(--ui-text-primary)', fontWeight: 600 }}>${position.allocatedAmount?.toFixed(2)}</div></div>
                                    <div><div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>P&L</div><PnLBadge value={position.pnlPercent ?? 0} /></div>
                                    <div><div style={{ color: 'var(--ui-text-muted)', fontSize: 11 }}>Returned</div><div style={{ color: '#1CD4A7', fontWeight: 600 }}>${((position.allocatedAmount ?? 0) + (position.pnlAmount ?? 0)).toFixed(2)}</div></div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}
